export type DiffSegment = { text: string; type: 'equal' | 'added' | 'removed' };

const MAX_DIFF_MATRIX_CELLS = 20_000;

/** Word-level LCS diff; whitespace tokens are preserved so spacing stays intact. */
export function computeWordDiff(oldStr: string, newStr: string): DiffSegment[] {
  if (oldStr === newStr) {
    return [{ text: oldStr, type: 'equal' }];
  }

  const oldWords = oldStr.split(/(\s+)/);
  const newWords = newStr.split(/(\s+)/);

  const m = oldWords.length;
  const n = newWords.length;

  // Keep user-provided descriptions from triggering quadratic work on the render path.
  if (m * n > MAX_DIFF_MATRIX_CELLS) {
    return [
      { text: oldStr, type: 'removed' },
      { text: newStr, type: 'added' },
    ];
  }

  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0) as number[]);
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldWords[i - 1] === newWords[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  const segments: DiffSegment[] = [];
  let i = m;
  let j = n;
  const raw: DiffSegment[] = [];
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      raw.push({ text: oldWords[i - 1], type: 'equal' });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      raw.push({ text: newWords[j - 1], type: 'added' });
      j--;
    } else {
      raw.push({ text: oldWords[i - 1], type: 'removed' });
      i--;
    }
  }
  raw.reverse();

  for (const seg of raw) {
    const last = segments[segments.length - 1];
    if (last && last.type === seg.type) {
      last.text += seg.text;
    } else {
      segments.push({ ...seg });
    }
  }
  return segments;
}
